import React, { useState } from 'react';
import axios from 'axios';
import { useI18n } from '../i18n';
import { fmtMoney } from '../lib/format';

// One line of an estimate, edited on its own screen — modelled on Joist's
// Item Details.
//
// It replaced a five-column inline grid where the description was a ~90px
// input. That was unusable on a phone for anything past a couple of words,
// which is the opposite of what a line item is for: the tech describing what
// they found and what it takes to fix it.
//
// Photos attach to the line rather than the estimate, so a line is a record of
// the work itself — what was found, or what was finished — and not just a
// price. They upload as they're picked, so submitting the estimate stays a
// plain JSON post carrying URLs.

// `variant` is which document the line belongs to. 'tech' is the original: a
// technician's estimate line, which is labour or materials and whose total is
// quantity times rate. 'doc' is a line on Julian's own estimate or invoice,
// where labour/materials is not a question the customer's paper asks, and
// where a fixed price with no workings behind it is the normal case — so the
// price is typed, and quantity × rate fills it in when both are given.
export default function ItemEditor({ item, index, total, onChange, onDelete, onClose, variant = 'tech', backLabel }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const isDoc = variant === 'doc';
  const set = (patch) => {
    const next = { ...item, ...patch };
    // Only compute when both are there: a lone quantity would otherwise wipe a
    // price he typed on purpose. Same rule the old estimate rows followed.
    if (isDoc && ('qty' in patch || 'unitCost' in patch)) {
      const q = parseFloat(next.qty), r = parseFloat(next.unitCost);
      if (!isNaN(q) && !isNaN(r)) next.price = String(Math.round(q * r * 100) / 100);
    }
    onChange(next);
  };

  const lineTotal = isDoc
    ? (parseFloat(item.price) || 0)
    : (parseFloat(item.qty) || 0) * (parseFloat(item.unitCost) || 0);

  const addPhotos = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post('/api/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set({ photos: [...(item.photos || []), ...(res.data.urls || [])] });
    } catch (err) {
      alert(err.response?.data?.message || 'Those photos could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal active" style={{ alignItems: 'stretch' }}>
        <div className="item-editor">
          <div className="item-editor-bar">
            <button className="item-editor-back" onClick={onClose}>‹ {backLabel || t('ie.items')}</button>
            <span className="item-editor-n">{index + 1} / {total}</span>
            <button className="item-editor-done" onClick={onClose}>{t('ie.done')}</button>
          </div>

          <div className="item-editor-body">
            <div className="item-editor-sec">{t('ie.itemDetails')}</div>

            <input
              className="item-editor-name"
              value={item.name || ''}
              onChange={e => set({ name: e.target.value })}
              placeholder={t('ie.itemName')}
            />

            <textarea
              className="item-editor-desc"
              value={item.description || ''}
              onChange={e => set({ description: e.target.value })}
              placeholder={t('ie.description')}
              rows={6}
            />

            <div className="item-editor-row">
              <span>{t('ie.photos')}</span>
              <span style={{ color: 'var(--muted)' }}>{(item.photos || []).length}</span>
            </div>
            <div className="item-editor-hint">{t('ie.photosHint')}</div>

            {(item.photos || []).length > 0 && (
              <div className="photo-strip" style={{ padding: '10px 16px' }}>
                {item.photos.map((url, i) => (
                  <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={url} alt="" className="photo-thumb" onClick={() => window.open(url, '_blank')} />
                    <button
                      className="item-editor-x"
                      aria-label={t('req.cancel')}
                      onClick={() => set({ photos: item.photos.filter((_, idx) => idx !== i) })}
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            <label className="item-editor-add">
              {busy ? t('req.submitting') : t('ie.addPhotos')}
              <input
                type="file" accept="image/*" multiple hidden disabled={busy}
                onChange={e => { addPhotos(Array.from(e.target.files)); e.target.value = ''; }}
              />
            </label>

            {!isDoc && (
              <div className="item-editor-row">
                <span>{t('ie.kind')}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['labor', 'material'].map(k => (
                    <button
                      key={k}
                      className={`btn btn-sm ${item.kind === k ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => set({ kind: k })}
                    >{k === 'labor' ? t('pj.labor') : t('pj.materials')}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="item-editor-row">
              <span>{t('ie.quantity')}</span>
              <input
                className="item-editor-num" type="number" inputMode="decimal" min="0" step="any"
                value={item.qty} onChange={e => set({ qty: e.target.value })} placeholder="—"
              />
            </div>

            <div className="item-editor-row">
              <span>{t('ie.rate')}</span>
              <input
                className="item-editor-num" type="number" inputMode="decimal" min="0" step="0.01"
                value={item.unitCost} onChange={e => set({ unitCost: e.target.value })}
                placeholder="0.00"
              />
            </div>

            {isDoc && (
              <div className="item-editor-hint">
                Quantity and rate are optional workings — fill both in and the price is worked out for you.
                Leave them and type the price, which is what a fixed-price line has always been.
              </div>
            )}

            <div className="item-editor-row item-editor-total">
              <span>{isDoc ? 'Price' : t('ie.lineTotal')}</span>
              {isDoc ? (
                <input
                  className="item-editor-num" type="number" inputMode="decimal" min="0" step="0.01"
                  value={item.price ?? ''} onChange={e => onChange({ ...item, price: e.target.value })}
                  placeholder="0.00"
                />
              ) : (
                <span>{fmtMoney(lineTotal)}</span>
              )}
            </div>

            <div style={{ padding: '22px 16px 40px' }}>
              <button className="btn btn-ghost btn-lg" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={onDelete}>
                {t('ie.removeLine')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}
